import { DashboardWidget, WidgetConfig, UserRole, WidgetCategory, WidgetSize } from './types';
import { canAccessFeature, ROLE_NAMES } from '@/lib/utils/role-access';

/**
 * Safely extract role name from user object with type checking
 */
const getUserRoleName = (user: any): UserRole | undefined => {
  const roleName = user?.role?.name;
  if (!roleName || typeof roleName !== 'string') return undefined;

  // Check if it's a valid role name
  return Object.values(ROLE_NAMES).includes(roleName as UserRole)
    ? roleName as UserRole
    : undefined;
};

/**
 * Central registry for all dashboard widgets
 * Manages widget registration, retrieval, and permission checking
 */
class WidgetRegistry {
  private widgets = new Map<string, DashboardWidget>();
  private roleWidgetConfigs = new Map<UserRole, WidgetConfig[]>();

  /**
   * Register a new widget in the registry
   */
  register(widget: DashboardWidget): void {
    if (this.widgets.has(widget.id)) {
      console.warn(`Widget with id "${widget.id}" is already registered. Overwriting...`);
    }
    this.widgets.set(widget.id, widget);
  }

  /**
   * Unregister a widget from the registry
   */
  unregister(widgetId: string): void {
    this.widgets.delete(widgetId);
  }

  /**
   * Get a widget by its ID
   */
  get(widgetId: string): DashboardWidget | undefined {
    return this.widgets.get(widgetId);
  }

  /**
   * Get all registered widgets
   */
  getAll(): DashboardWidget[] {
    return Array.from(this.widgets.values());
  }

  /**
   * Get widgets available for a specific role
   */
  getWidgetsForRole(role: UserRole): DashboardWidget[] {
    return this.getAll()
      .filter(widget => widget.roles.includes(role))
  }

  /**
   * Get widgets available for a user based on their permissions
   */
  getWidgetsForUser(user: any): DashboardWidget[] {
    const role = getUserRoleName(user);
    if (!role) return [];

    return this.getAll()
      .filter(widget => {
        // Check if user has the role
        if (!widget.roles.includes(role)) return false;

        // Check if user has required permissions
        return widget.permissions.every(permission =>
          canAccessFeature(permission, role)
        );
      })
  }

  /**
   * Get widgets by category
   */
  getWidgetsByCategory(category: WidgetCategory): DashboardWidget[] {
    return this.getAll()
      .filter(widget => widget.category === category)
  }

  /**
   * Check if a widget is available for a user
   */
  isWidgetAvailableForUser(widgetId: string, user: any): boolean {
    const widget = this.get(widgetId);
    if (!widget) return false;

    const role = getUserRoleName(user);
    if (!role || !widget.roles.includes(role)) return false;

    return widget.permissions.every(permission =>
      canAccessFeature(permission, role)
    );
  }

  /**
   * Set default widget configuration for a role
   */
  setRoleWidgetConfig(role: UserRole, configs: WidgetConfig[]): void {
    this.roleWidgetConfigs.set(role, configs);
  }

  /**
   * Get default widget configuration for a role
   */
  getRoleWidgetConfig(role: UserRole): WidgetConfig[] {
    return this.roleWidgetConfigs.get(role) || [];
  }

  /**
   * Get the number of registered widgets
   */
  size(): number {
    return this.widgets.size;
  }

  /**
   * Clear all registered widgets (useful for testing)
   */
  clear(): void {
    this.widgets.clear();
    this.roleWidgetConfigs.clear();
  }
}

// Export singleton instance
export const widgetRegistry = new WidgetRegistry();

/**
 * Helper function to register multiple widgets at once
 */
export function registerWidgets(widgets: DashboardWidget[]): void {
  widgets.forEach(widget => widgetRegistry.register(widget));
}

/**
 * Helper function to create a widget configuration
 */
export function createWidgetConfig(
  widgetId: string,
  position: number,
  size: WidgetSize = 'medium',
  enabled: boolean = true,
  customConfig?: Record<string, any>
): WidgetConfig {
  return {
    widgetId,
    position,
    size,
    enabled,
    customConfig
  };
}